import type { CareCommunicationAckType } from '@/hooks/useCareCommunications';
import { formatMedcomDateTime } from '@/lib/medcom/parseCareCommunicationBundle';

/** A later send of the same letter. Communicate writes this; the screen only reads it. */
export type MessageResend = {
  messageId: string;
  sentAt: string;
};

/** One send or missing-ack resend. Communicate writes this; the screen only reads it. */
export type MessageTransmissionAttempt = 0 | 1 | 2 | 3;
export type MessageTransmissionKind = 'initial' | 'resend';

export type MessageTransmission = {
  attempt: MessageTransmissionAttempt;
  sentAt: string;
  kind: MessageTransmissionKind;
};

export type VisibleTransmission = {
  attempt: MessageTransmissionAttempt;
  sentAt: string;
  kind: MessageTransmissionKind;
  time: string;
};

export function acknowledgementLabel(ackType: CareCommunicationAckType): string {
  switch (ackType) {
    case 'AA':
      return 'Positiv';
    case 'AE':
      return 'Fejl';
    case 'AR':
      return 'Afvist';
    default: {
      const unexpected: never = ackType;
      return unexpected;
    }
  }
}

export type ReceiptStatusTone = 'positive' | 'negative' | 'neutral';

/**
 * Letter status follows the newest receipt: AA Positiv, AE Fejl, AR Afvist.
 * After an Afvist that has been resent, and before a newer receipt, the status
 * says the letter is being sent again.
 */
export function receiptStatusPresentation(ackType: CareCommunicationAckType): {
  label: string;
  tone: ReceiptStatusTone;
} {
  switch (ackType) {
    case 'AA':
      return { label: acknowledgementLabel(ackType), tone: 'positive' };
    case 'AE':
      return { label: acknowledgementLabel(ackType), tone: 'negative' };
    case 'AR':
      return { label: acknowledgementLabel(ackType), tone: 'negative' };
    default: {
      const unexpected: never = ackType;
      return unexpected;
    }
  }
}

export function receiptStatusClass(tone: ReceiptStatusTone): string {
  switch (tone) {
    case 'positive':
      return 'bg-ds-green-tint text-ds-green-ink';
    case 'negative':
      return 'bg-ds-red-tint text-ds-red-ink';
    case 'neutral':
      return 'bg-ds-neutral-tint text-ds-text';
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
}

export type AcknowledgementCardSource = {
  id: string;
  ackType: CareCommunicationAckType;
  errorMessage: string | null;
  createdAt: string;
  duplicateResend?: boolean;
};

export type AcknowledgementCard = {
  id: string;
  label: string;
  positive: boolean;
  errorMessage: string | null;
  heading: string;
  /** When this receipt was stored, Denmark local time. */
  receivedAtLabel: string | null;
  /** AR: a technical send failure. The original outcome text stays in errorMessage. */
  notice: string | null;
  /** Set on the Afvist card once Communicate has resent the original letter. */
  resentNote: string | null;
  resendHeading: string | null;
  resendMessageId: string | null;
};

const TECHNICAL_SEND_FAILURE =
  'Der skete en teknisk fejl ved afsendelsen. Brevet sendes igen automatisk.';

const RESEND_STATUS = 'Afvist — sendes igen';

/** Clock in Denmark, 24-hour, for the duplicate-resend label. */
export function formatAckClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
  if (!hour || !minute) return '';
  return `${hour}:${minute}`;
}

function parsedTime(iso: string): number | null {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : time;
}

/**
 * Resend written on the original letter. Communicate may send
 * `resend: { messageId, sentAt }`, or `resendMessageId` plus `resentAt`.
 */
export function readMessageResend(raw: Record<string, unknown>): MessageResend | null {
  const nested = asRecord(raw.resend);
  const nestedId = typeof nested?.messageId === 'string' ? nested.messageId : null;
  const nestedAt = typeof nested?.sentAt === 'string' ? nested.sentAt : null;
  if (nestedId && nestedAt) return { messageId: nestedId, sentAt: nestedAt };

  const messageId = typeof raw.resendMessageId === 'string' ? raw.resendMessageId : null;
  const sentAt =
    typeof raw.resentAt === 'string'
      ? raw.resentAt
      : typeof raw.resendSentAt === 'string'
        ? raw.resendSentAt
        : null;
  if (messageId && sentAt) return { messageId, sentAt };
  return null;
}

/** True when this outbound row is Communicate's automatic resend of an Afvist letter. */
export function isAutomaticResend(message: {
  sendType?: string | null;
  activityCode?: string | null;
}): boolean {
  return message.sendType === 'resend' || message.activityCode === 'resend';
}

function isTransmissionAttempt(value: unknown): value is MessageTransmissionAttempt {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

function isTransmissionKind(value: unknown): value is MessageTransmissionKind {
  return value === 'initial' || value === 'resend';
}

/**
 * Optional Communicate `transmissions` on a list/thread message.
 * Missing or unreadable values are ignored so the screen still works.
 */
export function readMessageTransmissions(
  raw: Record<string, unknown>,
): MessageTransmission[] | undefined {
  if (!('transmissions' in raw)) return undefined;
  if (!Array.isArray(raw.transmissions)) return undefined;
  const items: MessageTransmission[] = [];
  for (const entry of raw.transmissions) {
    const record = asRecord(entry);
    if (!record) continue;
    if (!isTransmissionAttempt(record.attempt)) continue;
    if (typeof record.sentAt !== 'string' || !record.sentAt) continue;
    if (!isTransmissionKind(record.kind)) continue;
    items.push({
      attempt: record.attempt,
      sentAt: record.sentAt,
      kind: record.kind,
    });
  }
  return items;
}

/** Optional Communicate flag after the missing-ack resend budget is used up. */
export function readAckTimeoutExhausted(raw: Record<string, unknown>): boolean | undefined {
  if (!('ackTimeoutExhausted' in raw)) return undefined;
  return raw.ackTimeoutExhausted === true;
}

/** Outbound send times that can be labelled. Oldest attempt first. */
export function visibleTransmissions(
  transmissions: MessageTransmission[] | undefined,
): VisibleTransmission[] {
  if (!transmissions || transmissions.length === 0) return [];
  const visible: VisibleTransmission[] = [];
  for (const transmission of transmissions) {
    const time = formatMedcomDateTime(transmission.sentAt);
    if (!time) continue;
    visible.push({ ...transmission, time });
  }
  return visible.sort((left, right) => left.attempt - right.attempt);
}

export function transmissionLabel(
  transmission: VisibleTransmission,
  t: (key: string, options: { time: string; n?: number }) => string,
): string {
  switch (transmission.kind) {
    case 'initial':
      return t('communicate.transmission_sent', { time: transmission.time });
    case 'resend':
      return t('communicate.transmission_resent', {
        n: transmission.attempt,
        time: transmission.time,
      });
    default: {
      const unexpected: never = transmission.kind;
      return unexpected;
    }
  }
}

/**
 * Newest outbound row that points at the original letter. Communicate marks it
 * with `sendType` or `activityCode` `resend` and `parentMessageId`. A `resend`
 * object already on the original letter is used when that is what was written.
 */
export function resendForOriginal(
  messageId: string,
  own: MessageResend | null,
  outbound: Array<{
    id: string;
    parentMessageId: string | null;
    sentAt: string | null;
    createdAt: string;
    sendType: string | null;
    activityCode: string | null;
  }>,
): MessageResend | null {
  if (own) return own;
  let best: MessageResend | null = null;
  let bestTime = Number.NEGATIVE_INFINITY;
  for (const row of outbound) {
    if (row.parentMessageId !== messageId || !isAutomaticResend(row)) continue;
    const sentAt = row.sentAt ?? row.createdAt;
    const time = parsedTime(sentAt);
    if (time === null || time < bestTime) continue;
    bestTime = time;
    best = { messageId: row.id, sentAt };
  }
  return best;
}

function resendFollowsAck(sentAt: string, ackCreatedAt: string): boolean {
  const sent = parsedTime(sentAt);
  const created = parsedTime(ackCreatedAt);
  if (sent === null || created === null) return true;
  return sent >= created;
}

/** Newest Afvist that this resend answers. Receipts must already be newest-first. */
function acknowledgementIdForResend(
  acks: AcknowledgementCardSource[],
  resend: MessageResend,
): string | null {
  for (const ack of acks) {
    if (ack.ackType !== 'AR') continue;
    if (resendFollowsAck(resend.sentAt, ack.createdAt)) return ack.id;
  }
  return null;
}

export function statusForLatestReceipt(
  ackType: CareCommunicationAckType,
  ackCreatedAt: string,
  resend: MessageResend | null | undefined,
): { label: string; tone: ReceiptStatusTone } {
  if (ackType === 'AR' && resend && resendFollowsAck(resend.sentAt, ackCreatedAt)) {
    return { label: RESEND_STATUS, tone: 'negative' };
  }
  return receiptStatusPresentation(ackType);
}

export function duplicateImportFeedback(resent: boolean): string {
  if (resent) return 'Meddelelsen er allerede indlæst — ny positiv kvittering sendt.';
  return 'Meddelelsen er allerede indlæst.';
}

function ackTime(iso: string): number {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/** Newest receipt first, so the one just loaded is the line at the top. */
export function latestAcknowledgementFirst<T extends { createdAt: string }>(acks: T[]): T[] {
  return [...acks].sort((left, right) => ackTime(right.createdAt) - ackTime(left.createdAt));
}

export function applyAcknowledgementErrorTexts<
  T extends { id: string; errorMessage: string | null },
>(acks: T[], outcomeTexts: Record<string, string | null> | undefined): T[] {
  if (!outcomeTexts) return acks;
  return acks.map((ack) => {
    if (ack.errorMessage) return ack;
    const text = outcomeTexts[ack.id];
    if (!text) return ack;
    return { ...ack, errorMessage: text };
  });
}

export function acknowledgementCards(acks: AcknowledgementCardSource[]): AcknowledgementCard[] {
  return acks.map((ack, index) => {
    const label = acknowledgementLabel(ack.ackType);
    const positive = ack.ackType === 'AA';
    const resent = ack.duplicateResend === true || (ack.duplicateResend !== false && index > 0);
    const clock = formatAckClock(ack.createdAt);
    const heading =
      resent && clock
        ? `Kvittering — ${label} (gensendt for dublet, kl. ${clock})`
        : `Kvittering — ${label}`;
    return {
      id: ack.id,
      label,
      positive,
      errorMessage: ack.errorMessage,
      heading,
      receivedAtLabel: formatMedcomDateTime(ack.createdAt),
      notice: ack.ackType === 'AR' ? TECHNICAL_SEND_FAILURE : null,
      resentNote: null,
      resendHeading: null,
      resendMessageId: null,
    };
  });
}

function withResend(
  cards: AcknowledgementCard[],
  resend: MessageResend,
  ackId: string,
): AcknowledgementCard[] {
  const when = formatMedcomDateTime(resend.sentAt);
  if (!when) return cards;
  return cards.map((card) => {
    if (card.id !== ackId) return card;
    return {
      ...card,
      resentNote: `Sendt igen ${when}`,
      resendHeading: `Gensendt — ${when}`,
      resendMessageId: resend.messageId,
    };
  });
}

/** Cards newest-first, and the status of whichever receipt is on top. */
export function receiptsForLetter(
  acks: AcknowledgementCardSource[],
  outcomeTexts?: Record<string, string | null>,
  resend?: MessageResend | null,
): {
  cards: AcknowledgementCard[];
  status: { label: string; tone: ReceiptStatusTone } | null;
} {
  const ordered = latestAcknowledgementFirst(applyAcknowledgementErrorTexts(acks, outcomeTexts));
  const latest = ordered[0];
  const resendAckId = resend ? acknowledgementIdForResend(ordered, resend) : null;
  const cards =
    resend && resendAckId
      ? withResend(acknowledgementCards(ordered), resend, resendAckId)
      : acknowledgementCards(ordered);
  return {
    cards,
    status: latest ? statusForLatestReceipt(latest.ackType, latest.createdAt, resend) : null,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function decodeXml(value: string): string {
  return value
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&apos;', "'");
}

function issueTexts(issue: unknown): string[] {
  const issues = Array.isArray(issue) ? issue : issue ? [issue] : [];
  const texts: string[] = [];
  for (const item of issues) {
    const record = asRecord(item);
    if (!record) continue;
    const details = asRecord(record.details);
    const text = typeof details?.text === 'string' ? details.text.trim() : '';
    const diagnostics = typeof record.diagnostics === 'string' ? record.diagnostics.trim() : '';
    if (text) texts.push(text);
    else if (diagnostics) texts.push(diagnostics);
  }
  return texts;
}

function outcomeTextsFromBundle(bundle: Record<string, unknown>): string[] {
  const texts: string[] = [];
  if (bundle.resourceType === 'OperationOutcome') texts.push(...issueTexts(bundle.issue));
  const entries = Array.isArray(bundle.entry) ? bundle.entry : [];
  for (const entry of entries) {
    const resource = asRecord(asRecord(entry)?.resource);
    if (!resource || resource.resourceType !== 'OperationOutcome') continue;
    texts.push(...issueTexts(resource.issue));
  }
  return texts;
}

/** OperationOutcome.issue.details.text from a stored acknowledgement bundle. */
export function operationOutcomeText(payload: unknown): string | null {
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('<')) {
      const texts = [...trimmed.matchAll(/<text\s+value="([^"]*)"/g)]
        .map((match) => decodeXml(match[1] ?? '').trim())
        .filter((text) => text.length > 0);
      const unique = [...new Set(texts)];
      return unique.length > 0 ? unique.join('\n') : null;
    }
    try {
      return operationOutcomeText(JSON.parse(trimmed) as unknown);
    } catch {
      return null;
    }
  }
  const bundle = asRecord(payload);
  if (!bundle) return null;
  const unique = [...new Set(outcomeTextsFromBundle(bundle))];
  return unique.length > 0 ? unique.join('\n') : null;
}
